/**
 * Makes a horizontally-scrollable table a self-contained scroll region: fills to the
 * viewport bottom, pins multi-row `<thead>` rows, and adds a fixed bottom "proxy"
 * scrollbar reachable at any vertical position. The host calls `update()` after render
 * and `destroy()` on teardown; all DOM/listener lifecycle lives here.
 *
 * @param {Function} opts.getWrap  - returns the current scroll wrapper element (or null)
 * @param {Function} [opts.getRoot] - element to observe for size/visibility changes
 * @returns {{ update: Function, destroy: Function }}
 */
export function createStickyTableScroll({ getWrap, getRoot, bottomGap = 16, minHeight = 240 }) {
  let proxy = null;
  let proxyInner = null;
  let boundWrap = null;
  let resizeObserver = null;
  let syncing = false;

  const onProxyScroll = () => {
    const wrap = getWrap();
    if (!wrap || syncing) {
      return;
    }
    syncing = true;
    wrap.scrollLeft = proxy.scrollLeft;
    syncing = false;
  };

  const onWrapScroll = () => {
    const wrap = getWrap();
    if (!wrap || !proxy || syncing) {
      return;
    }
    syncing = true;
    proxy.scrollLeft = wrap.scrollLeft;
    syncing = false;
  };

  const onResize = () => update();
  const onScrollCapture = () => syncProxy();

  function bindWrapScroll() {
    const wrap = getWrap();
    if (wrap === boundWrap) {
      return;
    }
    if (boundWrap) {
      boundWrap.removeEventListener('scroll', onWrapScroll);
    }
    if (wrap) {
      wrap.addEventListener('scroll', onWrapScroll, { passive: true });
    }
    boundWrap = wrap;
  }

  function syncProxy() {
    const wrap = getWrap();
    if (!proxy) {
      return;
    }
    if (!wrap || !wrap.offsetParent) {
      proxy.style.display = 'none';
      return;
    }

    const rect = wrap.getBoundingClientRect();
    proxy.style.left = `${rect.left}px`;
    proxy.style.width = `${rect.width}px`;
    proxyInner.style.width = `${wrap.scrollWidth}px`;

    const hasOverflow = wrap.scrollWidth > wrap.clientWidth + 1;
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    const nativeBarVisible = rect.bottom <= window.innerHeight;
    proxy.style.display = hasOverflow && inView && !nativeBarVisible ? 'block' : 'none';

    if (!syncing) {
      syncing = true;
      proxy.scrollLeft = wrap.scrollLeft;
      syncing = false;
    }
  }

  function update() {
    bindWrapScroll();

    const wrap = getWrap();
    if (!wrap || !wrap.offsetParent) {
      syncProxy();
      return;
    }

    const top = wrap.getBoundingClientRect().top;
    const maxHeight = `${Math.max(window.innerHeight - top - bottomGap, minHeight)}px`;
    if (wrap.style.maxHeight !== maxHeight) {
      wrap.style.maxHeight = maxHeight;
    }

    // Overlap each row 2px under the previous to cover the collapsed-border/sub-pixel seam.
    let offset = 0;
    wrap.querySelectorAll('thead tr').forEach((row, index) => {
      const top = index === 0 ? 0 : offset - 2;
      row.querySelectorAll('th').forEach((th) => {
        th.style.top = `${top}px`;
      });
      offset += row.offsetHeight;
    });

    syncProxy();
  }

  function destroy() {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScrollCapture, true);

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (boundWrap) {
      boundWrap.removeEventListener('scroll', onWrapScroll);
      boundWrap = null;
    }
    if (proxy) {
      proxy.removeEventListener('scroll', onProxyScroll);
      if (proxy.parentNode) {
        proxy.parentNode.removeChild(proxy);
      }
      proxy = null;
      proxyInner = null;
    }
  }

  // --- init ---
  proxy = document.createElement('div');
  proxyInner = document.createElement('div');
  proxyInner.style.height = '1px';
  proxy.appendChild(proxyInner);
  proxy.style.cssText =
    'position:fixed;bottom:0;z-index:1004;overflow-x:auto;overflow-y:hidden;display:none;';
  proxy.addEventListener('scroll', onProxyScroll, { passive: true });
  document.body.appendChild(proxy);

  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onScrollCapture, true);

  const root = typeof getRoot === 'function' ? getRoot() : null;
  if (root && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => update());
    resizeObserver.observe(root);
  }

  return { update, destroy };
}
